<script>
import { DirectUpload } from 'activestorage';
import { mapGetters, mapActions } from 'vuex';
import FileHeader from './FileHeader.vue';
import FullEditor from 'dashboard/components/widgets/WootWriter/FullEditor.vue';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

export default {
  components: {
    FileHeader,
    FullEditor,
  },
  props: {
    knowledgebaseId: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      content: '',
      fileName: '',
      debouncedGenerateFileName: () => {},
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
      activeRecord: 'records/getActiveRecord',
    }),
  },
  watch: {
    async activeRecord(newVal, oldVal) {
      if (!newVal) {
        this.content = '';
        this.fileName = '';
        return;
      }

      if (!newVal.file) return;
      if (oldVal && newVal.id == oldVal.id) return;

      const url = newVal.file.url;
      const content = await this.downloadFileContent(url);

      this.content = content;
      this.fileName = newVal.file.filename;
    },
  },
  activated() {
    this.init();
  },
  mounted() {
    this.debouncedGenerateFileName = debounce(this.generateFileName, 300);
  },
  methods: {
    ...mapActions({
      getRecord: 'records/getRecord',
      clearRecord: 'records/clearRecord',
    }),
    async init() {
      if (this.recordId) {
        this.getRecord({
          knowledgebaseId: this.knowledgebaseId,
          recordId: this.recordId,
        });
      } else {
        this.clearRecord();
      }
    },
    async downloadFileContent(url) {
      const response = await fetch(url);
      const data = await response.text();
      return data;
    },
    async saveFile() {
      const blob = new Blob([this.content], { type: 'text/markdown' });
      const file = new File([blob], this.fileName, { type: 'text/markdown' });
      await this.uploadMarkdownFile(file);
      this.$emit('close');
    },
    async deleteFile() {
      if (!this.recordId) {
        this.$router.push({
          name: 'settings_knowledgebases_content',
          params: { knowledgebaseId: this.knowledgebaseId },
        });
        return;
      }

      try {
        await this.$store.dispatch('records/delete', {
          knowledgebaseId: this.knowledgebaseId,
          recordId: this.recordId,
        });
        useAlert(this.$t('LIBRARIES.FILE.API.DELETE_SUCCESS_MESSAGE'));
        this.$router.push({
          name: 'settings_knowledgebases_content',
          params: { knowledgebaseId: this.knowledgebaseId },
        });
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('LIBRARIES.FILE.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
    close() {
      this.$emit('close');
    },
    async uploadMarkdownFile(file) {
      const upload = new DirectUpload(
        file,
        `/api/v1/accounts/${this.currentAccountId}/knowledgebases/${this.knowledgebaseId}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          useAlert(error);
        } else {
          this.attachFile({ file, upload: blob });
        }
      });
    },
    attachFile({ file, upload }) {
      this.addRecord(upload);
    },
    async addRecord(upload) {
      try {
        const record = await this.$store.dispatch('records/create', {
          record: { file: upload.signed_id },
          knowledgebaseId: this.knowledgebaseId,
        });
        useAlert(this.$t('LIBRARIES.FILE.API.SUCCESS_MESSAGE'));
        this.$router.push({
          name: 'settings_knowledgebases_record_markdown_editor',
          params: {
            knowledgebaseId: this.knowledgebaseId,
            recordId: record.id,
          },
        });
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('LIBRARIES.FILE.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
    updateFileName(newName) {
      this.fileName = newName;
    },
    generateFileName(content) {
      if (!this.fileName && content) {
        const firstFewWords = content.split(' ').slice(0, 3).join('_');
        this.fileName =
          firstFewWords.toLowerCase().replace(/[^a-z0-9_]/g, '') + '.md';
      }
    },
    onContentInput(content) {
      this.debouncedGenerateFileName(content);
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto h-full">
    <FileHeader
      :name="fileName"
      @update-name="updateFileName"
      @save="saveFile"
      @delete="deleteFile"
    />
    <div class="p-16 bg-white">
      <div class="code-font">
        <FullEditor
          v-model="content"
          placeholder="Create your markdown content here"
          @input="onContentInput"
        />
      </div>
    </div>
  </div>
</template>

<style>
.code-font {
  font-family:
    Consolas,
    Monaco,
    Lucida Console,
    Liberation Mono,
    DejaVu Sans Mono,
    Bitstream Vera Sans Mono,
    Courier New;
}
</style>
